import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  IconButton,
  MobileStepper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FolderSharedRounded from "@mui/icons-material/FolderSharedRounded";
import HealthAndSafetyRounded from "@mui/icons-material/HealthAndSafetyRounded";
import MedicationRounded from "@mui/icons-material/MedicationRounded";
import { db } from "services/firebase";
import HelpVideo from "components/common/HelpVideo";
import patientVideo from "assets/help/patient-walkthrough.mp4";
import patientCaptions from "assets/help/patient-walkthrough.vtt";
import patientPoster from "assets/help/patient-walkthrough-poster.jpg";

const FEATURES = [
  {
    icon: <HealthAndSafetyRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "CareStart",
    description:
      "Not sure what to do? Describe how you're feeling and CareStart helps you understand what kind of care you may need.",
  },
  {
    icon: <FolderSharedRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "Your MasterChart",
    description:
      "Keep your conditions, medications, allergies, and history in one place, and share it with a provider.",
  },
  {
    icon: <MedicationRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "My Medications",
    description:
      "Keep your medication list current, and review discharge instructions from facility visits.",
  },
];

const FeatureRow = ({ icon, title, description }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
      p: 2,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      textAlign: "left",
    }}
  >
    {icon}
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </Box>
);

const PatientOnboardingDialog = ({ open, onClose, userId }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  const handleClose = () => {
    onClose();

    if (dontShowAgain && userId) {
      db.collection("users")
        .doc(userId)
        .update({ hideOnboardingDialog: true })
        .catch((error) => {
          console.error("Error saving onboarding preference:", error);
        });
    }
  };

  const steps = [
    {
      content: (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <HealthAndSafetyRounded color="primary" sx={{ fontSize: 64, mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 3 }}>
            Welcome to My HealthDesk
          </Typography>
          <Typography variant="body1" paragraph>
            My HealthDesk gives you clear, reliable answers about your health in
            plain language.
          </Typography>
          <Typography variant="body1" paragraph>
            Check your symptoms, look things up, and keep your health record all
            in one place with MasterChart.
          </Typography>
        </Box>
      ),
    },
    {
      content: (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            See how it works
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            A quick tour of what you can do with My HealthDesk.
          </Typography>
          <HelpVideo
            src={patientVideo}
            captions={patientCaptions}
            poster={patientPoster}
            caption="Watch a quick tour of My HealthDesk."
          />
        </Box>
      ),
    },
    {
      content: (
        <Box sx={{ py: 2 }}>
          <Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
            What you can do
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {FEATURES.map((feature) => (
              <FeatureRow key={feature.title} {...feature} />
            ))}
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxWidth: 680 },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 4, pb: 1 }}>
        {steps[activeStep].content}

        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={activeStep}
          sx={{
            background: "transparent",
            justifyContent: "center",
            mt: 2,
          }}
          nextButton={null}
          backButton={null}
        />
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", p: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
          }
          label="Don't show this again"
        />
        <Box>
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
          )}

          {activeStep === steps.length - 1 ? (
            <Button variant="contained" onClick={handleClose}>
              Get Started
            </Button>
          ) : (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PatientOnboardingDialog;
